import { useGetFavoritesQuery, useGetTopAssetsQuery } from '@carboncopyco/data';
import { type TopAssetType, TopAssetTypes } from '@carboncopyco/types';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useConsoleState } from './ConsoleContext';
import { TopAssetsTable } from './TopAssetsTable';
import { TopAssetsTableControls } from './TopAssetsTableControls';
import { TopAssetsTableRowWrapper } from './TopAssetsTableRowWrapper';
import { useTableControls } from './useTopAssetsTableControls';
import { getTypeFilterOptionsByAvailableAssets } from './utils';

export const MyTopAssetsTable = () => {
  const { organizationId } = useParams();
  const { startTime, endTime } = useConsoleState();
  const { sort, selectedTypes } = useTableControls();

  const assetTypes = useMemo(
    () => Object.keys(selectedTypes) as TopAssetType[],
    [selectedTypes]
  );

  const myTopAssetsCommonArgs = useMemo(
    () => ({
      startTime,
      endTime,
      organizationId,
    }),
    [startTime, endTime, organizationId]
  );

  const myTopAssetsArgs = useMemo(
    () => ({
      ...myTopAssetsCommonArgs,
      myAssets: true,
      excludeFavorites: true,
      assetTypes,
      sort,
    }),
    [myTopAssetsCommonArgs, assetTypes, sort]
  );

  const {
    data: { items: myTopAssets = [] } = {},
    isLoading: isLoadingMyTopAssets,
  } = useGetTopAssetsQuery(myTopAssetsArgs);

  const getFavoritesArgs = useMemo(
    () => ({
      ...myTopAssetsCommonArgs,
      assetTypes,
    }),
    [myTopAssetsCommonArgs, assetTypes]
  );
  const allAssetTypes = Object.values(TopAssetTypes);

  const {
    data: { items: favorites = [] } = {},
    isLoading: isLoadingFavorites,
  } = useGetFavoritesQuery(getFavoritesArgs);

  const {
    data: { items: myTopAssetsTotal = [] } = {},
  } = useGetTopAssetsQuery({
    ...myTopAssetsCommonArgs,
    myAssets: true,
    excludeFavorites: true,
    assetTypes: allAssetTypes,
    sort: 'last_updated',
  });

  const {
    data: { items: favoriteAssetsTotal = [] } = {},
  } = useGetFavoritesQuery({
    ...myTopAssetsCommonArgs,
    assetTypes: allAssetTypes,
  });

  const topAssets = [...favorites, ...myTopAssets];

  const isEmpty =
    !(isLoadingMyTopAssets || isLoadingFavorites) && !topAssets.length;

  const typeFilterOptions = getTypeFilterOptionsByAvailableAssets([
    ...myTopAssetsTotal,
    ...favoriteAssetsTotal,
  ]);

  if (topAssets && !topAssets.length) {
    return null;
  }

  return (
    <div className="max-h-[650px]">
      <TopAssetsTable
        title="Top resources"
        topAssets={topAssets}
        isLoading={isLoadingMyTopAssets || isLoadingFavorites}
        renderRow={({ item: topAsset, children }) => (
          <TopAssetsTableRowWrapper
            asset={topAsset}
            getTopAssetsArgs={myTopAssetsArgs}
            getFavoritesArgs={getFavoritesArgs}
          >
            {children}
          </TopAssetsTableRowWrapper>
        )}
        controls={
          <TopAssetsTableControls
            isEmpty={isEmpty}
            typeFilterOptions={typeFilterOptions}
          />
        }
      />
    </div>
  );
};
